import React, { useState, useMemo } from "react";
import { ShieldCheck, Download, RefreshCw, Activity, AlertTriangle, UserCog, Users, ChevronDown, ChevronRight, History } from "lucide-react";
import {
    PageHeader, Button, KpiTile, ObjectStatus, Avatar, SearchField, SelectField, TableCard, EmptyState, MessageStrip,
    surface, thCls, tdCls,
} from "./fiori";
import { useAuditEvents, filterEvents, downloadCsv, actorName, relTime, fullTime, inPeriod, ACTOR_KIND, PERIODS } from "../audit/auditData";

// Audit log for the Hasbro workspace: every data entry and change in Hasbro's business —
// by Hasbro staff, by customer logins, and by platform admins acting on Hasbro's users.
// Same component pattern any future tenant gets (GET /api/audit/tenant).

const SEV_TONE = { high: "negative", medium: "critical", low: "information" };

export default function HasbroAudit() {
    const { events, tenantName, categories, loading, error, reload, loadOlder } = useAuditEvents("/api/audit/tenant");
    const [f, setF] = useState({ q: "", period: "30d", category: "", severity: "", kind: "" });
    const [open, setOpen] = useState(null);
    const set = k => v => setF(x => ({ ...x, [k]: v }));
    const shown = useMemo(() => filterEvents(events, f), [events, f]);
    const last30 = useMemo(() => events.filter(e => inPeriod(e, "30d")), [events]);
    const stats = {
        total: last30.length,
        high: last30.filter(e => e.severity === "high").length,
        platform: last30.filter(e => e.actor?.kind === "platform-admin").length,
        people: new Set(last30.map(e => e.actor?.email || e.actor?.uid).filter(Boolean)).size,
    };

    return (
        <div className="max-w-7xl mx-auto px-1 sm:px-2 py-4 space-y-5">
            <PageHeader
                breadcrumb={[{ label: tenantName || "Hasbro" }, { label: "Governance" }]}
                icon={ShieldCheck}
                title="Audit Log"
                subtitle="Every entry and change in your workspace — who did what, when, and what it was before. Written by the server; nobody can edit it."
                actions={<>
                    <Button icon={RefreshCw} onClick={reload} disabled={loading}>Refresh</Button>
                    <Button variant="emphasized" icon={Download} onClick={() => downloadCsv(shown, { tenants: { hasbro: tenantName }, file: "hasbro-audit" })} disabled={!shown.length}>Export CSV</Button>
                </>}
            >
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiTile label="Actions (30 days)" value={stats.total} icon={Activity} tone="information" />
                    <KpiTile label="High severity" value={stats.high} icon={AlertTriangle} tone={stats.high ? "negative" : "neutral"} active={f.severity === "high"} onClick={() => setF(x => ({ ...x, severity: x.severity === "high" ? "" : "high" }))} hint="Deletes, disables, passwords, permissions" />
                    <KpiTile label="By platform admins" value={stats.platform} icon={UserCog} tone="indication" active={f.kind === "platform-admin"} onClick={() => setF(x => ({ ...x, kind: x.kind === "platform-admin" ? "" : "platform-admin" }))} hint="TinyGlobal staff acting on your users" />
                    <KpiTile label="People active" value={stats.people} icon={Users} tone="positive" />
                </div>
            </PageHeader>

            {error && <MessageStrip type="error">{error}</MessageStrip>}

            <section className={`${surface} p-4 space-y-3`}>
                <SearchField value={f.q} onChange={set("q")} placeholder="Search who, what, ticket, customer, file or a changed value" className="w-full" />
                <div className="flex flex-wrap items-end gap-3">
                    <div className="inline-flex rounded-lg border border-slate-300 dark:border-white/10 p-0.5">
                        {PERIODS.map(([id, label]) => (
                            <button key={id} onClick={() => set("period")(id)} className={`px-3 h-8 rounded-md text-xs font-semibold ${f.period === id ? "bg-[#0087C9] text-white" : "text-slate-600 dark:text-slate-300"}`}>{label}</button>
                        ))}
                    </div>
                    <SelectField label="Area" value={f.category} onChange={set("category")} className="w-44">
                        <option value="">All areas</option>
                        {Object.entries(categories || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </SelectField>
                    <SelectField label="Severity" value={f.severity} onChange={set("severity")} className="w-36">
                        <option value="">All</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                    </SelectField>
                    <SelectField label="Who" value={f.kind} onChange={set("kind")} className="w-44">
                        <option value="">Anyone</option>
                        {Object.entries(ACTOR_KIND).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </SelectField>
                    <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">{shown.length} of {events.length} loaded</span>
                </div>
            </section>

            <TableCard title="Activity" count={shown.length}>
                {loading && !events.length ? (
                    <div className="p-5 space-y-3">{[0, 1, 2, 3].map(i => <div key={i} className="h-10 rounded-lg bg-slate-100 dark:bg-white/[0.05] animate-pulse" />)}</div>
                ) : !shown.length ? (
                    <EmptyState icon={History} title={events.length ? "Nothing matches these filters" : "No activity recorded yet"}
                        text={events.length ? "Try a longer period or clear the filters." : "Tickets, customer accounts, contacts, emails, report uploads and user changes are recorded here from now on."} />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr>{["What happened", "Who", "When", ""].map(h => <th key={h} className={thCls}>{h}</th>)}</tr></thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-white/[0.05]">
                                {shown.map(e => {
                                    const isOpen = open === e.id;
                                    return (
                                        <React.Fragment key={e.id}>
                                            <tr onClick={() => setOpen(isOpen ? null : e.id)} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-white/[0.03] align-top">
                                                <td className={tdCls}>
                                                    <div className="flex items-start gap-2.5">
                                                        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${e.severity === "high" ? "bg-red-500" : e.severity === "medium" ? "bg-amber-500" : "bg-[#0087C9]"}`} />
                                                        <div className="min-w-0">
                                                            <p className="text-[#1D2D3E] dark:text-slate-100"><b>{actorName(e)}</b> {e.summary}</p>
                                                            {e.changes?.length > 0 && (
                                                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                                    {e.changes.slice(0, 4).map((c, i) => (
                                                                        <span key={i} className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-white/[0.06] text-[11px] text-slate-600 dark:text-slate-300">
                                                                            {c.field}: <span className="line-through opacity-60">{c.from}</span> → <b>{c.to}</b>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{categories?.[e.category] || e.category}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className={tdCls}>
                                                    <span className="flex items-center gap-2.5">
                                                        <Avatar name={actorName(e)} size={30} />
                                                        <span className="min-w-0">
                                                            <span className="block font-semibold text-[#1D2D3E] dark:text-white truncate max-w-[200px]">{actorName(e)}</span>
                                                            <ObjectStatus tone={ACTOR_KIND[e.actor?.kind]?.tone || "neutral"}>{ACTOR_KIND[e.actor?.kind]?.label || "System"}</ObjectStatus>
                                                        </span>
                                                    </span>
                                                </td>
                                                <td className={`${tdCls} whitespace-nowrap text-xs text-slate-500 dark:text-slate-400`} title={fullTime(e.at)}>{relTime(e.at)}</td>
                                                <td className={`${tdCls} text-right`}>
                                                    <ObjectStatus tone={SEV_TONE[e.severity] || "neutral"}>{e.severity}</ObjectStatus>
                                                    {isOpen ? <ChevronDown size={14} className="inline ml-1 text-slate-400" /> : <ChevronRight size={14} className="inline ml-1 text-slate-300" />}
                                                </td>
                                            </tr>
                                            {isOpen && (
                                                <tr className="bg-slate-50/70 dark:bg-white/[0.02]">
                                                    <td colSpan={4} className="px-5 py-4">
                                                        <Detail e={e} categories={categories} />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {loadOlder && events.length > 0 && (
                    <div className="border-t border-slate-100 dark:border-white/[0.06] p-3 text-center">
                        <Button variant="transparent" onClick={loadOlder} disabled={loading}>{loading ? "Loading…" : "Load older activity"}</Button>
                    </div>
                )}
            </TableCard>
        </div>
    );
}

function Detail({ e, categories }) {
    const rows = [
        ["When", fullTime(e.at)], ["Who", `${actorName(e)}${e.actor?.email && e.actor.email !== actorName(e) ? ` <${e.actor.email}>` : ""}`],
        ["Role", [ACTOR_KIND[e.actor?.kind]?.label, e.actor?.role].filter(Boolean).join(" · ") || "—"], ["Area", categories?.[e.category] || e.category || "—"],
        ["Record", e.entity ? `${e.entity.type}${e.entity.label ? ` · ${e.entity.label}` : ""}` : "—"], ["Action code", e.action],
        ["From", e.ip ? `${e.ip}${e.userAgent ? ` · ${e.userAgent}` : ""}` : e.actor?.kind === "platform-admin" ? "Platform staff (not shown)" : "—"], ["Event id", e.id],
    ];
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <dl className="grid grid-cols-[100px_1fr] gap-x-3 gap-y-1.5">
                {rows.map(([k, v]) => <React.Fragment key={k}><dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 pt-0.5">{k}</dt><dd className="text-[#1D2D3E] dark:text-slate-200 break-all">{v}</dd></React.Fragment>)}
            </dl>
            {e.changes?.length > 0 && (
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">Before → after</p>
                    <table className="w-full text-xs rounded-lg border border-slate-200 dark:border-white/[0.07] overflow-hidden">
                        <thead><tr><th className={thCls}>Field</th><th className={thCls}>Before</th><th className={thCls}>After</th></tr></thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-white/[0.05]">
                            {e.changes.map((c, i) => <tr key={i}><td className="px-4 py-1.5 font-semibold">{c.field}</td><td className="px-4 py-1.5 text-slate-500">{c.from}</td><td className="px-4 py-1.5">{c.to}</td></tr>)}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
